import {
  Component,
  ArrowLeftRight,
  Layers,
  HeartPulse,
  ShieldLock,
} from "lucide-react";
import { useEffect, useRef } from "react";
import { motion, useInView, useMotionValue, useSpring } from "framer-motion";
import bg from "../../assets/images/growing-up.png";
// Animated counter component for stat numbers
function Counter({ value, suffix = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const motionVal = useMotionValue(0);
  const springVal = useSpring(motionVal, { damping: 30, stiffness: 100 });

  useEffect(() => {
    if (inView) motionVal.set(value);
  }, [inView, value, motionVal]);

  useEffect(() => {
    return springVal.on("change", (latest) => {
      if (ref.current) {
        ref.current.textContent = Math.floor(latest) + suffix;
      }
    });
  }, [springVal, suffix]);

  return (
    <span className="font-serif font-semibold tracking-wider" ref={ref}>
      0{suffix}
    </span>
  );
}
export default function CamundaSolution() {
  return (
    <section className="relative w-full overflow-hidden bg-[#ffffff] lg:py-24 md:py-16 py-10 text-slate-900">
      <div
        className="pointer-events-none absolute top-0 left-0 w-full h-full opacity-20 "
        style={{
          background:
            "linear-gradient(135deg, #62d4f7 0%, #5dc9f0 40%, #3579be 60%",
        }}
      />
      <div className="relative z-10 mx-auto flex flex-col items-center px-6 lg:px-12 3xl:w-4/5 xl:w-[90%]">
        <div className="mb-16 flex max-w-4xl flex-col items-center text-center md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-4 inline-flex items-center gap-2 rounded-full  px-3.5 py-1.5 backdrop-blur-md"
          >
            <span className="text-[11px] font-bold uppercase tracking-[0.2em] px-6 lg:px-0 text-slate-700">
              Enterprise BPM Solutions
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.08 }}
            className="font-display text-3xl sm:text-4xl md:text-5xl font-bold px-6 lg:px-0 tracking-tight text-slate-950 "
          >
            Our{" "}
            <span className="italic font-serif font-normal text-[#5dc9f0]">
              Services
            </span>{" "}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15 }}
            className="mt-5 max-w-2xl text-base font-normal leading-relaxed px-6 lg:px-0 text-slate-600 sm:text-lg"
          >
            End-to-End Expertise across Process Design, Implementation &
            Modernization — powered by Truviq's OrchestrAI platform.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-8 rounded-xl bg-[#ffffff] border border-slate-300/80 backdrop-blur-xl shadow-[0_15px_35px_rgba(0,0,0,0.03)] flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between pb-4 mb-5 border-b border-slate-200/80">
                <div className="flex items-center gap-2.5">
                  <div className="h-10 w-10 rounded-xl bg-white border border-slate-300 text-slate-950 flex items-center justify-center shadow-xs">
                    <Component size={24} />
                  </div>
                  <h3 className="font-display text-base font-bold text-slate-950 tracking-tight">
                    Process Design & Modeling
                  </h3>
                </div>
              </div>
              <p className="text-base text-slate-600 font-normal leading-relaxed">
                Expert BPMN 2.0 and DMN 3.0 modeling aligned with ISO standards.
                Production-ready Camunda process definitions with governance
                best practices.
              </p>
            </div>
          </motion.div>

           <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className=" rounded-xl bg-[#000036] text-white md:p-8 p-6 flex flex-col justify-between relative overflow-hidden shadow-md"
          >
            <div className="pointer-events-none absolute -top-12 -right-12 w-44 h-44 bg-violet-600/30 rounded-full blur-3xl" />
            <div className="pointer-events-none absolute top-0 right-0 w-3/5 opacity-60 z-0">
              <img src={bg} alt="" />
            </div>

            <div>
              <span className="text-xs font-semibold tracking-widest text-slate-400 uppercase">
               Truviq Platform
              </span>
              <div className="mt-2 text-5xl font-light tracking-tighte text-white">
                <Counter value={50} suffix="%" />
              </div>
            </div>

            <div className="mt-8">
              <p className="text-base font-medium text-slate-200">
               OrchestAI‑Powered Delivery
              </p>
              <p className="mt-1 text-base text-slate-400 leading-relaxed">
                Auto-generate BPMN models & cut dev time by 50%.
              </p>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-8 rounded-xl bg-[#ffffff] border border-slate-300/80 backdrop-blur-xl shadow-[0_15px_35px_rgba(0,0,0,0.03)] flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between pb-4 mb-5 border-b border-slate-200/80">
                <div className="flex items-center gap-2.5">
                  <div className="h-10 w-10 rounded-xl bg-white border border-slate-300 text-slate-950 flex items-center justify-center shadow-xs">
                    <ArrowLeftRight size={24} />
                  </div>
                  <h3 className="font-display text-base font-bold text-slate-950 tracking-tight">
                    Legacy BPM Migration
                  </h3>
                </div>
              </div>
              <p className="text-base text-slate-600 font-normal leading-relaxed">
                Accelerated migration from Pega, IBM BPM, and Appian to Camunda
                8 using OrchestrAI automation with zero business disruption.
              </p>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-8 rounded-xl bg-[#ffffff] border border-slate-300/80 backdrop-blur-xl shadow-[0_15px_35px_rgba(0,0,0,0.03)] flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between pb-4 mb-5 border-b border-slate-200/80">
                <div className="flex items-center gap-2.5">
                  <div className="h-10 w-10 rounded-xl bg-white border border-slate-300 text-slate-950 flex items-center justify-center shadow-xs">
                    <Layers size={24} />
                  </div>
                  <h3 className="font-display text-base font-bold text-slate-950 tracking-tight">
                    Full-Stack Implementation
                  </h3>
                </div>
              </div>
              <p className="text-base text-slate-600 font-normal leading-relaxed">
                End-to-end development of Spring Boot Java workers, REST
                connectors, and React UI scaffolding for Camunda 8 solutions.
              </p>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-8 rounded-xl bg-[#ffffff] border border-slate-300/80 backdrop-blur-xl shadow-[0_15px_35px_rgba(0,0,0,0.03)] flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between pb-4 mb-5 border-b border-slate-200/80">
                <div className="flex items-center gap-2.5">
                  <div className="h-10 w-10 rounded-xl bg-white border border-slate-300 text-slate-950 flex items-center justify-center shadow-xs">
                    <ShieldLock size={24} />
                  </div>
                  <h3 className="font-display text-base font-bold text-slate-950 tracking-tight">
                    BPMN Linting & AI Governance
                  </h3>
                </div>
              </div>
              <p className="text-base text-slate-600 font-normal leading-relaxed">
                Automated BPMN quality checks enforcing Truviq best practices,
                gateway patterns, error handling, and AI governance guardrails
                across your entire process estate.
              </p>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-8 rounded-xl bg-[#ffffff] border border-slate-300/80 backdrop-blur-xl shadow-[0_15px_35px_rgba(0,0,0,0.03)] flex flex-col justify-between"
          >
            <div>
              <div className="flex items-center justify-between pb-4 mb-5 border-b border-slate-200/80">
                <div className="flex items-center gap-2.5">
                  <div className="h-10 w-10 rounded-xl bg-white border border-slate-300 text-slate-950 flex items-center justify-center shadow-xs">
                    <HeartPulse size={24} />
                  </div>
                  <h3 className="font-display text-base font-bold text-slate-950 tracking-tight">
                    Healthcare & FHIR Integration
                  </h3>
                </div>
              </div>
              <p className="text-base text-slate-600 font-normal leading-relaxed">
                Specialized Camunda connectors for FHIR R4 resources with
                SMART-on-FHIR OAuth2 security and healthcare-grade workflows.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
